// アプリの唯一の結線点。
//
// 役割: DuckDB 起動状態、地図ビュー、描画、API 設定、レイアウトを保持し、
//       レイヤー操作（useLayerActions）・ファイル取込（useImportFlow）・データテーブル
//       （useDataActions）・エージェント（useAgentSession）・音声（useVoiceSession）の
//       フックを依存順に結線する。各フックは推論・SQL 実行・データ管理を agent/* ・voice/* ・
//       data/* ・duckdb/* のモジュールへ委ね、ここは結線に徹する。
// 関係: hooks/*（結線フック）、agent/*・tools/*・data/*・components/*。
//       UI コンポーネントは表示と入力のみを担い、推論・SQL 実行はここから下のモジュールが行う。
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { WebMercatorViewport } from '@deck.gl/core'

import { query } from './duckdb/db'
import { importGeoJSON } from './data/layer-service'
import { clearSessionStorage } from './data/session-storage'

import { useLayerActions } from './hooks/useLayerActions'
import { useImportFlow } from './hooks/useImportFlow'
import { useDataActions } from './hooks/useDataActions'
import { useAgentSession } from './hooks/useAgentSession'
import { useVoiceSession } from './hooks/useVoiceSession'

import Header from './components/Header'
import Sidebar from './components/Sidebar'
import TabbedPanel from './components/TabbedPanel'
import MapView from './components/MapView'
import FileUpload from './components/FileUpload'
import LayerPanel from './components/LayerPanel'
import DataPanel from './components/DataPanel'
import ExecutionLog from './components/ExecutionLog'
import DrawPanel from './components/DrawPanel'
import CrsConfirmModal from './components/CrsConfirmModal'
import VoiceButton from './components/VoiceButton'
import AboutModal from './components/AboutModal'
import ChatPanel from './components/ChatPanel'
import ApiSettings from './components/ApiSettings'

import './styles/app.css'

// 初期ビューポート（日本中心）。
const INITIAL_VIEW_STATE = {
  latitude: 35.732,
  longitude: 137.533,
  bearing: 0,
  pitch: 0,
  zoom: 4,
}

// API 設定の localStorage キーと既定値。
const API_KEY_STORAGE = 'web-gis-agent.apiKey'
const MODEL_STORAGE = 'web-gis-agent.model'
const MAX_TOKENS_STORAGE = 'web-gis-agent.maxTokens'
const LOG_STORAGE = 'web-gis-agent.operation-log'
const DEFAULT_MODEL = 'claude-opus-4-8'
const DEFAULT_MAX_TOKENS = 16000

// 音声機能（Gemini Live）の設定。Claude とは別のキー・モデルを使う。
const GEMINI_API_KEY_STORAGE = 'web-gis-agent.geminiApiKey'
const VOICE_MODEL_STORAGE = 'web-gis-agent.voiceModel'
const DEFAULT_VOICE_MODEL = 'gemini-3.1-flash-live-preview'

// イントロダクションを初回のみ自動表示するための既読フラグ。
const INTRO_STORAGE = 'web-gis-agent.introSeen'

// 描画中の図形を貯める空の FeatureCollection。
const EMPTY_FC = { type: 'FeatureCollection', features: [] }

function loadSetting(key, fallback = '') {
  try {
    return globalThis.localStorage?.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function loadLogs() {
  try {
    const parsed = JSON.parse(globalThis.localStorage?.getItem(LOG_STORAGE) ?? '[]')
    return Array.isArray(parsed) ? parsed.slice(-300) : []
  } catch {
    return []
  }
}

function App() {
  // --- 地図ビュー ---
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE)

  // --- DuckDB 起動状態（smoke test） ---
  const [status, setStatus] = useState('initializing') // initializing | ready | error
  const [errorMessage, setErrorMessage] = useState('')

  // --- 操作・実行ログ ---
  const [logs, setLogs] = useState(loadLogs)
  const log = useCallback((message) => {
    setLogs((cur) => [...cur, { id: crypto.randomUUID(), message }].slice(-300))
  }, [])

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(LOG_STORAGE, JSON.stringify(logs))
    } catch {
      // 保存できなくても現在の画面上のログは継続する。
    }
  }, [logs])

  // --- 下部タブの選択状態 ---
  const [activeTab, setActiveTab] = useState('layers')

  // --- API 設定 ---
  const [apiKey, setApiKey] = useState(() => loadSetting(API_KEY_STORAGE))
  const [model, setModel] = useState(() => loadSetting(MODEL_STORAGE) || DEFAULT_MODEL)
  const [maxTokens, setMaxTokens] = useState(
    () => Number(loadSetting(MAX_TOKENS_STORAGE)) || DEFAULT_MAX_TOKENS,
  )
  const [geminiApiKey, setGeminiApiKey] = useState(() => loadSetting(GEMINI_API_KEY_STORAGE))
  const [voiceModel, setVoiceModel] = useState(
    () => loadSetting(VOICE_MODEL_STORAGE) || DEFAULT_VOICE_MODEL,
  )
  const [settingsOpen, setSettingsOpen] = useState(false)

  // --- イントロダクション（About）。初回アクセス時（既読フラグ無し）のみ自動表示する。 ---
  const [aboutOpen, setAboutOpen] = useState(() => !loadSetting(INTRO_STORAGE))

  // --- パネル開閉・幅（左=管理タブ / 右=エージェント） ---
  const [leftOpen, setLeftOpen] = useState(true)
  const [rightOpen, setRightOpen] = useState(true)
  // 既定はパネル最小幅（Sidebar の minWidth と一致）。地図を広く使えるようにする。
  const [leftWidth, setLeftWidth] = useState(240)
  const [rightWidth, setRightWidth] = useState(240)
  // 狭幅（縦積み）レイアウトでのパネル高さ。null = CSS 既定（%）、ドラッグで px 上書き。
  const [leftHeight, setLeftHeight] = useState(null)
  const [rightHeight, setRightHeight] = useState(null)

  // --- 描画（点/線/面 → 新レイヤー） ---
  const [drawMode, setDrawMode] = useState('none') // none | point | line | polygon
  const [drawnFeatures, setDrawnFeatures] = useState(EMPTY_FC)
  const [drawName, setDrawName] = useState('drawing_1')
  const drawSeqRef = useRef(1)

  // DuckDB + Spatial の起動確認（plan.md §4.1）。
  useEffect(() => {
    let cancelled = false
    async function check() {
      try {
        await query('SELECT ST_AsText(ST_Point(0, 0));')
        if (!cancelled) setStatus('ready')
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(String(e?.message ?? e))
          setStatus('error')
        }
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [])

  // bounds にビューをフィットさせる。
  const fitToBounds = useCallback((bounds) => {
    if (!bounds) return
    const [[minX, minY], [maxX, maxY]] = bounds
    if (minX === maxX || minY === maxY) {
      setViewState((v) => ({
        ...v,
        longitude: (minX + maxX) / 2,
        latitude: (minY + maxY) / 2,
        zoom: 12,
        transitionDuration: 500,
      }))
      return
    }
    const vp = new WebMercatorViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    })
    const { longitude, latitude, zoom } = vp.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      { padding: 60 },
    )
    setViewState((v) => ({ ...v, longitude, latitude, zoom, transitionDuration: 500 }))
  }, [])

  // --- レイヤー操作（追加/削除/表示切替/ズーム/エクスポート/undo-redo） ---
  const {
    layers,
    addLayerToMap,
    dropLayerFromMap,
    setLayerStyle,
    handleToggle,
    handleZoom,
    handleRemove,
    handleExport,
    handleDescribe,
    handleAppearanceChange,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    clearLayers,
  } = useLayerActions({ fitToBounds, log, ready: status === 'ready' })

  // --- ファイル取込（CRS 不明時はモーダルへ退避） ---
  const { busy, pendingImport, handleFiles, handleCrsConfirm, handleCrsCancel } = useImportFlow({
    addLayerToMap,
    log,
  })

  // --- データタブ（非空間 CSV → SQL JOIN 用のデータテーブル） ---
  const {
    dataTables,
    busy: dataBusy,
    handleDataFiles,
    handleRemoveDataTable,
    clearDataTables,
  } = useDataActions({ log, ready: status === 'ready' })

  // --- エージェント（チャット）セッション ---
  const {
    messages,
    isRunning,
    chatInput,
    setChatInput,
    chatInputRef,
    handleSubmit,
    handleAbort,
    handleResetChat,
    appendToInput,
  } = useAgentSession({
    addLayerToMap,
    dropLayerFromMap,
    setLayerStyle,
    layers,
    dataTables,
    apiKey,
    model,
    maxTokens,
    log,
  })

  // --- 音声セッション（Gemini Live）---
  // Gemini にはアプリのツールを渡さず、プロンプト入力欄への書き込みと地図画像の取得だけを許す。
  const {
    voiceState,
    transcript: voiceTranscript,
    error: voiceError,
    elapsed: voiceElapsed,
    start: startVoice,
    stop: stopVoice,
  } = useVoiceSession({
    apiKey: geminiApiKey,
    model: voiceModel,
    layers,
    dataTables,
    isAgentRunning: isRunning,
    setChatInput,
    chatInputRef,
    log,
    // 書き込み先が見えるよう、埋めるときは右パネル（エージェント）を開く。
    onFillPrompt: () => setRightOpen(true),
  })

  // 「新しい会話」は会話だけでなく、その会話で扱った全レイヤー／データも初期化する。
  const handleNewConversation = useCallback(async () => {
    handleAbort()
    await Promise.all([clearLayers(), clearDataTables()])
    await clearSessionStorage()
    handleResetChat()
    setLogs([])
    setDrawnFeatures(EMPTY_FC)
    setDrawMode('none')
  }, [handleAbort, clearLayers, clearDataTables, handleResetChat])

  // レイヤー行の「@」: レイヤー id をプロンプト入力欄の末尾に追記し、右パネルを開いてフォーカス。
  const handleMentionLayer = useCallback(
    (layerId) => {
      setRightOpen(true)
      appendToInput(layerId)
    },
    [appendToInput],
  )

  // --- 描画ハンドラ ---
  // EditableGeoJsonLayer の編集結果を受けて、貯めている FeatureCollection を更新する。
  const handleDrawEdit = useCallback((updatedData) => {
    setDrawnFeatures(updatedData ?? EMPTY_FC)
  }, [])

  const handleClearDrawing = useCallback(() => setDrawnFeatures(EMPTY_FC), [])

  // 描いた図形をまとめて 1 つの新レイヤーとして取り込む（取込パイプラインに合流）。
  const handleMaterializeDrawing = useCallback(async () => {
    const features = drawnFeatures.features ?? []
    if (features.length === 0) return
    try {
      const layer = await importGeoJSON(drawnFeatures, { displayName: drawName.trim() || 'drawing' })
      addLayerToMap(layer)
      log(`描画レイヤー化: ${layer.displayName}（${layer.featureCount} 件）`)
      // 後始末: 描画クリア・モード解除・次のレイヤー名を採番。
      setDrawnFeatures(EMPTY_FC)
      setDrawMode('none')
      drawSeqRef.current += 1
      setDrawName(`drawing_${drawSeqRef.current}`)
    } catch (e) {
      log(`描画レイヤー化エラー: ${String(e?.message ?? e)}`)
    }
  }, [drawnFeatures, drawName, addLayerToMap, log])

  // --- API 設定の保存・削除 ---
  const handleSaveSettings = useCallback(() => {
    // 貼り付け時に紛れ込む前後の空白・改行を除去してから保存する（ヘッダ不正の予防）。
    const cleanKey = apiKey.trim()
    if (cleanKey !== apiKey) setApiKey(cleanKey)
    const cleanGeminiKey = geminiApiKey.trim()
    if (cleanGeminiKey !== geminiApiKey) setGeminiApiKey(cleanGeminiKey)
    try {
      globalThis.localStorage?.setItem(API_KEY_STORAGE, cleanKey)
      globalThis.localStorage?.setItem(MODEL_STORAGE, model)
      globalThis.localStorage?.setItem(MAX_TOKENS_STORAGE, String(maxTokens))
      globalThis.localStorage?.setItem(GEMINI_API_KEY_STORAGE, cleanGeminiKey)
      globalThis.localStorage?.setItem(VOICE_MODEL_STORAGE, voiceModel)
    } catch {
      // 保存失敗は致命的でないため握りつぶす（メモリ上の設定は有効）。
    }
    setSettingsOpen(false)
  }, [apiKey, model, maxTokens, geminiApiKey, voiceModel])

  const handleDeleteKey = useCallback(() => {
    setApiKey('')
    setGeminiApiKey('')
    try {
      globalThis.localStorage?.removeItem(API_KEY_STORAGE)
      globalThis.localStorage?.removeItem(GEMINI_API_KEY_STORAGE)
    } catch {
      // 無視
    }
  }, [])

  // イントロを閉じ、既読フラグを保存して以後は自動表示しないようにする。
  const handleCloseAbout = useCallback(() => {
    setAboutOpen(false)
    try {
      globalThis.localStorage?.setItem(INTRO_STORAGE, '1')
    } catch {
      // 保存失敗は致命的でないため握りつぶす（このセッション内は再表示されない）。
    }
  }, [])

  const chatDisabled = status !== 'ready' || !apiKey
  const chatDisabledReason =
    status !== 'ready'
      ? 'DuckDB の起動を待っています…'
      : !apiKey
        ? '⚙ API設定 から Claude APIキーを設定してください。'
        : ''

  // 下部タブの構成（レイヤー / データ / ログ）。
  const tabs = useMemo(
    () => [
      {
        id: 'layers',
        label: 'レイヤー',
        content: (
          <div className="layers-tab">
            <FileUpload onFiles={handleFiles} busy={busy} />
            <LayerPanel
              layers={layers}
              onToggle={handleToggle}
              onZoom={handleZoom}
              onRemove={handleRemove}
              onExport={handleExport}
              onLoadPreview={handleDescribe}
              onAppearanceChange={handleAppearanceChange}
              onMention={handleMentionLayer}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={canUndo}
              canRedo={canRedo}
            />
          </div>
        ),
      },
      {
        id: 'data',
        label: 'データ',
        content: (
          <DataPanel
            dataTables={dataTables}
            busy={dataBusy}
            onDataFiles={handleDataFiles}
            onRemove={handleRemoveDataTable}
            onMention={handleMentionLayer}
          />
        ),
      },
      { id: 'log', label: 'ログ', content: <ExecutionLog logs={logs} /> },
    ],
    [
      layers,
      logs,
      busy,
      dataTables,
      dataBusy,
      handleDataFiles,
      handleRemoveDataTable,
      canUndo,
      canRedo,
      handleFiles,
      handleToggle,
      handleZoom,
      handleRemove,
      handleExport,
      handleDescribe,
      handleAppearanceChange,
      handleMentionLayer,
      handleUndo,
      handleRedo,
    ],
  )

  // 地図上に浮かべる描画ツールバー（widget は使わず React オーバーレイで実現）。
  const drawOverlay = (
    <DrawPanel
      drawMode={drawMode}
      featureCount={drawnFeatures.features?.length ?? 0}
      name={drawName}
      onNameChange={setDrawName}
      onSelectMode={setDrawMode}
      onMaterialize={handleMaterializeDrawing}
      onClear={handleClearDrawing}
    />
  )

  return (
    <div className="app-shell">
      <Header
        status={status}
        errorMessage={errorMessage}
        leftOpen={leftOpen}
        rightOpen={rightOpen}
        onToggleLeft={() => setLeftOpen((v) => !v)}
        onToggleRight={() => setRightOpen((v) => !v)}
        onShowAbout={() => setAboutOpen(true)}
        settingsSlot={
          <ApiSettings
            apiKey={apiKey}
            model={model}
            maxTokens={maxTokens}
            geminiApiKey={geminiApiKey}
            voiceModel={voiceModel}
            isOpen={settingsOpen}
            onToggle={() => setSettingsOpen((v) => !v)}
            onApiKeyChange={setApiKey}
            onModelChange={setModel}
            onMaxTokensChange={setMaxTokens}
            onGeminiApiKeyChange={setGeminiApiKey}
            onVoiceModelChange={setVoiceModel}
            onSave={handleSaveSettings}
            onDeleteKey={handleDeleteKey}
          />
        }
      />
      <div className="workspace">
        {/* 左パネル: 管理タブ（レイヤー/ログ/SQL） */}
        <Sidebar
          side="left"
          open={leftOpen}
          width={leftWidth}
          onWidthChange={setLeftWidth}
          height={leftHeight}
          onHeightChange={setLeftHeight}
        >
          <TabbedPanel tabs={tabs} activeId={activeTab} onTabChange={setActiveTab} />
        </Sidebar>

        {/* 中央: 地図（残り全面）。描画モード時は編集レイヤーを重ねる。 */}
        <MapView
          viewState={viewState}
          onViewStateChange={setViewState}
          layers={layers}
          drawMode={drawMode}
          drawnFeatures={drawnFeatures}
          onDrawEdit={handleDrawEdit}
          overlay={drawOverlay}
        />

        {/* 右パネル: エージェント（チャット） */}
        <Sidebar
          side="right"
          open={rightOpen}
          width={rightWidth}
          onWidthChange={setRightWidth}
          height={rightHeight}
          onHeightChange={setRightHeight}
        >
          <ChatPanel
            messages={messages}
            isRunning={isRunning}
            disabled={chatDisabled}
            disabledReason={chatDisabledReason}
            input={chatInput}
            onInputChange={setChatInput}
            inputRef={chatInputRef}
            onSubmit={handleSubmit}
            onAbort={handleAbort}
            onReset={handleNewConversation}
            voiceSlot={
              <VoiceButton
                state={voiceState}
                transcript={voiceTranscript}
                error={voiceError}
                elapsed={voiceElapsed}
                disabled={!geminiApiKey}
                disabledReason="⚙ API設定 から Gemini APIキーを設定してください。"
                onStart={startVoice}
                onStop={stopVoice}
              />
            }
          />
        </Sidebar>
      </div>

      {pendingImport && (
        <CrsConfirmModal
          fileName={pendingImport.file.name}
          onConfirm={handleCrsConfirm}
          onCancel={handleCrsCancel}
        />
      )}

      {aboutOpen && <AboutModal onClose={handleCloseAbout} />}
    </div>
  )
}

export default App
